// src/pages/goodsReceivedNote/CreateGoodsReceivedNotePage.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Save, PackageCheck, X, Search, AlertCircle, Package } from 'lucide-react';
import { createGoodsReceivedNote } from '../../services/api/procurement/grn';
import { getPurchaseOrderById, getAllPurchaseOrders, getUserWarehouses } from '../../services/api/purchaseOrder';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import { InfoCard } from '../../components/common';
import ProductThumb from '../procurement/components/ProductThumb';
import QuantityStepper from '../procurement/components/QuantityStepper';
import ps from '../procurement/components/ProcurementDetails.module.css';
import { PURCHASE_ORDER_BASE_PATH, getReturnTarget } from '../../utils/procurementNavigation';
import styles from './CreateGoodsReceivedNotePage.module.css';

const CreateGoodsReceivedNotePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useLocalization();
  const { success, error } = useAlert();

  // Same numeric-id-vs-order-number dual lookup as Delivery Note's create page: no page here
  // (a PO details "Create GRN" button, a query-param prefill, or manual entry) is guaranteed to
  // supply a numeric PO id, so a plain by-number search has to be supported too.
  const [purchaseOrderIdInput, setPurchaseOrderIdInput] = useState(searchParams.get('purchaseOrderId') || '');
  const [purchaseOrder, setPurchaseOrder] = useState(null);
  const [poOptions, setPoOptions] = useState([]);
  const [isSearchingPo, setIsSearchingPo] = useState(false);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [supplierDeliveryReference, setSupplierDeliveryReference] = useState('');
  const [notes, setNotes] = useState('');
  const [itemQuantities, setItemQuantities] = useState({}); // purchaseOrderItemId -> quantity
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const defaultBackTarget = purchaseOrder ? `${PURCHASE_ORDER_BASE_PATH}/${purchaseOrder.id}` : '/procurement/grn';
  const defaultBackLabel = purchaseOrder?.purchaseOrderNumber || t('goodsReceivedNote.backToGoodsReceivedNotes');
  const backTarget = getReturnTarget(location, searchParams, defaultBackTarget, defaultBackLabel);

  useEffect(() => {
    (async () => {
      try {
        const result = await getUserWarehouses();
        setWarehouses(result.warehouses || []);
      } catch (err) {
        console.error('Failed to load warehouses:', err);
      }
    })();
  }, []);

  useEffect(() => {
    if (searchParams.get('purchaseOrderId')) {
      handleLookupPurchaseOrder(searchParams.get('purchaseOrderId'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toPoOption = (po) => ({
    value: po.id,
    label: `${po.purchaseOrderNumber}${po.Supplier?.name ? ` — ${po.Supplier.name}` : ''}`,
  });

  // Searchable lookup: open (pending/partial) purchase orders by number; an all-digit term is
  // also resolved as a PO id. Selecting an option runs the existing lookup.
  const searchPurchaseOrders = async (term = '') => {
    const value = term.trim();
    try {
      setIsSearchingPo(true);
      const { purchaseOrders } = await getAllPurchaseOrders({ search: value, limit: 20 });
      const results = (purchaseOrders || []).filter(po => ['pending', 'partial'].includes(po.status));
      if (/^\d+$/.test(value) && !results.some(po => String(po.id) === value)) {
        try {
          const { purchaseOrder: byId } = await getPurchaseOrderById(value);
          if (byId && ['pending', 'partial'].includes(byId.status)) results.unshift(byId);
        } catch (err) {
          // not a valid id — number search results only
        }
      }
      setPoOptions(results.map(toPoOption));
    } catch (err) {
      console.error('Failed to search purchase orders:', err);
      setPoOptions([]);
    } finally {
      setIsSearchingPo(false);
    }
  };

  useEffect(() => {
    searchPurchaseOrders('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyPurchaseOrder = (po) => {
    setPurchaseOrder(po);
    setPoOptions(prev => (prev.some(option => option.value === po.id) ? prev : [toPoOption(po), ...prev]));
    setPurchaseOrderIdInput(po.purchaseOrderNumber || String(po.id));
    setWarehouseId(po.warehouseId ? String(po.warehouseId) : '');

    const remainingByItem = {};
    (po.items || []).forEach(item => {
      const remaining = Math.max(0, parseFloat(item.quantity) - parseFloat(item.receivedQuantity || 0));
      remainingByItem[item.id] = remaining;
    });
    setItemQuantities(remainingByItem);
  };

  const handleLookupPurchaseOrder = async (idOverride) => {
    const value = (idOverride ?? purchaseOrderIdInput).trim();
    if (!value) return;
    try {
      setIsLookingUp(true);
      setLookupError('');
      let po;
      if (/^\d+$/.test(value)) {
        ({ purchaseOrder: po } = await getPurchaseOrderById(value));
      } else {
        const { purchaseOrders } = await getAllPurchaseOrders({ search: value });
        const match = (purchaseOrders || []).find(o => o.purchaseOrderNumber?.toLowerCase() === value.toLowerCase());
        if (!match) throw new Error(t('goodsReceivedNote.purchaseOrderNotFound'));
        // The search/list endpoint returns a lighter record (no nested Product/ProductVariation
        // per item) — re-fetch the full purchase order by id so the items table has complete data.
        ({ purchaseOrder: po } = await getPurchaseOrderById(match.id));
      }
      if (po.status === 'cancelled') {
        throw new Error(t('goodsReceivedNote.purchaseOrderCancelled'));
      }
      applyPurchaseOrder(po);
    } catch (err) {
      setPurchaseOrder(null);
      setLookupError(err.response?.data?.message || err.message || t('goodsReceivedNote.purchaseOrderNotFound'));
    } finally {
      setIsLookingUp(false);
    }
  };

  const remainingQty = (item) => Math.max(0, parseFloat(item.quantity) - parseFloat(item.receivedQuantity || 0));

  const updateQuantity = (purchaseOrderItemId, value, max) => {
    const clamped = Math.min(Math.max(0, parseInt(value, 10) || 0), max);
    setItemQuantities(prev => ({ ...prev, [purchaseOrderItemId]: clamped }));
  };

  const hasItemsSelected = Object.values(itemQuantities).some(q => q > 0);

  const validateForm = () => {
    const newErrors = {};
    if (!purchaseOrder) newErrors.order = t('goodsReceivedNote.purchaseOrderRequired');
    if (!warehouseId) newErrors.warehouse = t('goodsReceivedNote.warehouseRequired');
    if (!hasItemsSelected) newErrors.items = t('goodsReceivedNote.atLeastOneItemRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!validateForm()) {
      error(t('goodsReceivedNote.someFieldsRequired'), { title: t('common.validationError') });
      return;
    }

    setIsSubmitting(true);
    try {
      const itemsReceived = Object.entries(itemQuantities)
        .filter(([, qty]) => qty > 0)
        .map(([purchaseOrderItemId, qty]) => ({
          purchaseOrderItemId: parseInt(purchaseOrderItemId, 10),
          quantityReceived: qty,
        }));

      const payload = {
        purchaseOrderId: purchaseOrder.id,
        warehouseId: parseInt(warehouseId, 10),
        itemsReceived,
        supplierDeliveryReference: supplierDeliveryReference.trim() || undefined,
        notes: notes.trim() || undefined,
      };

      const response = await createGoodsReceivedNote(payload);
      success(t('goodsReceivedNote.goodsReceivedNoteCreatedSuccess', { number: response.grn?.grnNumber }), {
        title: t('common.success'),
      });
      navigate(`/procurement/grn/${response.grn.id}`);
    } catch (err) {
      console.error('Goods received note creation error:', err);
      error(err.response?.data?.message || t('goodsReceivedNote.failedToCreateGoodsReceivedNote'), {
        title: t('common.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalSelected = Object.values(itemQuantities).reduce((sum, q) => sum + q, 0);

  return (
    <div className={styles.createGoodsReceivedNotePage}>
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <PackageCheck className={styles.headerIcon} size={22} />
            <div className={styles.titleContent}>
              <h1>{t('goodsReceivedNote.createGoodsReceivedNote')}</h1>
              <p>{t('goodsReceivedNote.createGoodsReceivedNoteDescription')}</p>
            </div>
          </div>
          <button onClick={() => navigate(backTarget.path)} className={styles.backButton}>
            <ArrowLeft size={16} />
            {backTarget.label || t('goodsReceivedNote.backToGoodsReceivedNotes')}
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className={styles.grnForm}>
        <InfoCard icon={Search} title={t('goodsReceivedNote.lookupPurchaseOrder')}>
          <div className={styles.lookupRowWrapper}>
            <div className={styles.lookupRow}>
              <Dropdown
                className={styles.lookupDropdown}
                value={purchaseOrder?.id || ''}
                onChange={value => { if (value) handleLookupPurchaseOrder(String(value)); }}
                options={poOptions}
                placeholder={isLookingUp ? t('common.loading') : t('goodsReceivedNote.purchaseOrderIdPlaceholder')}
                searchPlaceholder={t('goodsReceivedNote.searchPurchaseOrder')}
                onSearch={searchPurchaseOrders}
                loading={isSearchingPo}
                loadingText={t('common.loading')}
                noOptionsText={t('goodsReceivedNote.noOpenPurchaseOrders')}
                allowClear={false}
                disabled={isLookingUp}
              />
            </div>
            {purchaseOrder && (
              <div className={styles.orderSummary}>
                <span><strong>{purchaseOrder.purchaseOrderNumber}</strong></span>
                <span>{purchaseOrder.Supplier?.name || t('common.na')}</span>
              </div>
            )}
          </div>
          {lookupError && <div className={styles.sectionError}><AlertCircle size={16} />{lookupError}</div>}
          {errors.order && <div className={styles.sectionError}><AlertCircle size={16} />{errors.order}</div>}
        </InfoCard>

        {purchaseOrder && (
          <div className={styles.topColumns}>
            <div className={styles.leftColumn}>
              <InfoCard icon={PackageCheck} title={t('goodsReceivedNote.details')}>
                <div className={styles.fieldsGrid}>
                  <div className={styles.fieldGroup}>
                    <label>{t('goodsReceivedNote.warehouseLabel')}</label>
                    <Dropdown
                      value={warehouseId}
                      onChange={value => setWarehouseId(value || '')}
                      options={warehouses.map(w => ({ value: w.id, label: w.name }))}
                      placeholder={t('goodsReceivedNote.selectWarehouse')}
                      searchable
                    />
                  </div>
                  <div className={styles.fieldGroup}>
                    <label>{t('goodsReceivedNote.supplierDeliveryReference')}</label>
                    <input
                      type="text"
                      value={supplierDeliveryReference}
                      onChange={(e) => setSupplierDeliveryReference(e.target.value)}
                      className={styles.input}
                      placeholder={t('goodsReceivedNote.supplierDeliveryReferencePlaceholder')}
                    />
                  </div>
                </div>
                <div className={`${styles.fieldGroup} ${styles.notesGroup}`}>
                  <label>{t('goodsReceivedNote.notes')}</label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    className={styles.textarea}
                    placeholder={t('goodsReceivedNote.notesPlaceholder')}
                  />
                </div>
                {errors.warehouse && <div className={styles.sectionError}><AlertCircle size={16} />{errors.warehouse}</div>}
              </InfoCard>
            </div>

            <div className={styles.rightColumn}>
              <InfoCard icon={Package} title={t('goodsReceivedNote.itemsToReceive')}>
                <div className={ps.tableWrapper}>
                <table className={ps.itemsTable}>
                  <thead>
                    <tr>
                      <th>{t('goodsReceivedNote.item')}</th>
                      <th className={ps.alignRight}>{t('goodsReceivedNote.orderedQty')}</th>
                      <th className={ps.alignRight}>{t('goodsReceivedNote.remainingQty')}</th>
                      <th className={ps.alignRight}>{t('goodsReceivedNote.quantityReceived')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(purchaseOrder.items || []).map(item => {
                      const remaining = remainingQty(item);
                      return (
                        <tr key={item.id}>
                          <td>
                            <div className={ps.productCellInner}>
                              <ProductThumb item={item} alt={item.Product?.name || ''} />
                              <div className={ps.productCell}>
                                <span className={ps.productName}>{item.Product?.name}</span>
                                {(item.ProductVariation?.sku || item.Product?.sku) && <span className={ps.productSku}>SKU: {(item.ProductVariation?.sku || item.Product?.sku)}</span>}
                              </div>
                            </div>
                          </td>
                          <td className={`${ps.alignRight} ${ps.amountCell}`}>{item.quantity}</td>
                          <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{remaining}</td>
                          <td className={ps.alignRight}>
                            <QuantityStepper
                              value={itemQuantities[item.id] ?? 0}
                              onChange={(value) => updateQuantity(item.id, value, remaining)}
                              min={0}
                              max={remaining}
                              disabled={remaining <= 0}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
                {errors.items && <div className={styles.sectionError}><AlertCircle size={16} />{errors.items}</div>}
              </InfoCard>
            </div>
          </div>
        )}

        <div className={styles.actionBarSpacer} />
      </form>

      <div className={styles.fixedActionBar}>
        <div className={styles.actionBarInner}>
          <div className={styles.actionBarMeta}>
            {totalSelected > 0 && (
              <span className={styles.actionBarItemCount}>
                {totalSelected} {t('goodsReceivedNote.unitsSelected')}
              </span>
            )}
          </div>
          <div className={styles.actionBarButtons}>
            <button type="button" onClick={() => navigate(backTarget.path)} className={styles.cancelButton} disabled={isSubmitting}>
              <X size={15} />
              {t('common.cancel')}
            </button>
            <button type="button" onClick={handleSubmit} className={styles.submitButton} disabled={isSubmitting || !purchaseOrder}>
              {isSubmitting ? <div className={styles.loadingSpinner} /> : (
                <>
                  <Save size={15} />
                  {t('goodsReceivedNote.createGoodsReceivedNote')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateGoodsReceivedNotePage;
